import { Navigate } from "react-router-dom";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import PageHero from "../components/site/PageHero";
import { PageSection, CheckGrid, NumberSteps, CTABand, DisclaimerBox } from "../components/site/ContentBlocks";
import { serviceLd } from "../components/site/SEO";
import { FadeUp } from "../components/site/Reveal";
import { COUNTRIES, HEALTHCARE_DISCLAIMER } from "../data/content";

export default function CountryPage({ cid }) {
    const country = COUNTRIES.find((c) => c.id === cid);
    if (!country) return <Navigate to="/healthcare-medical-tourism" replace />;

    return (
        <div className="grain bg-navy-deep" data-testid={`country-page-${country.id}`}>
            <Header />
            <main>
                <PageHero
                    seo={{
                        title: country.seoTitle,
                        description: country.seoDesc,
                        path: country.path,
                        jsonLd: serviceLd(`Medical Tourism in ${country.name}`, country.seoDesc, country.path),
                    }}
                    crumbs={[
                        { label: "Healthcare & Medical Tourism", href: "/healthcare-medical-tourism" },
                        { label: country.name },
                    ]}
                    eyebrow="Medical Tourism Destination"
                    title={`Medical Tourism in`}
                    titleEm={`${country.name}.`}
                    copy={country.why}
                />

                <section className="bg-navy-deep pb-4">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <FadeUp>
                            <div className="relative overflow-hidden border border-white/8">
                                <img
                                    src={country.image}
                                    alt={country.imageAlt}
                                    loading="lazy"
                                    className="h-[300px] w-full object-cover lg:h-[420px]"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/80 via-transparent to-transparent" aria-hidden="true" />
                                <span className="absolute bottom-5 left-6 text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">
                                    {country.name} — Healthcare Destination
                                </span>
                            </div>
                        </FadeUp>
                    </div>
                </section>

                <PageSection light eyebrow="Treatment Areas" title={`Main Treatment Areas in ${country.name}`} testid={`${country.id}-treatments`}>
                    <CheckGrid items={country.treatments} light cols={3} />
                    <div className="mt-14">
                        <h3 className="font-serif text-2xl font-normal text-navy-deep">Types of Healthcare Providers</h3>
                        <div className="mt-6">
                            <CheckGrid items={country.providers} light />
                        </div>
                    </div>
                </PageSection>

                <PageSection eyebrow="Our Role" title="How Viet Medica Coordinates Your Enquiry" testid={`${country.id}-coordination`}>
                    <FadeUp>
                        <p className="max-w-3xl text-base leading-relaxed text-slate-300/85 md:text-lg">{country.coordination}</p>
                    </FadeUp>
                    <div className="mt-12">
                        <h3 className="font-serif text-2xl font-normal text-ivory">Travel and Practical Planning</h3>
                        <div className="mt-6">
                            <CheckGrid items={country.travel} />
                        </div>
                    </div>
                </PageSection>

                <PageSection light eyebrow="Be Prepared" title="Important Questions Patients Should Ask" testid={`${country.id}-questions`}>
                    <NumberSteps steps={country.questions} light />
                </PageSection>

                <section className="bg-navy-deep py-20 lg:py-28">
                    <div className="mx-auto max-w-[1440px] space-y-10 px-5 lg:px-10">
                        <DisclaimerBox text={HEALTHCARE_DISCLAIMER} testid={`${country.id}-disclaimer`} />
                        <CTABand
                            title={`Planning treatment in ${country.name}?`}
                            primary={{ label: `${country.name} Enquiry`, to: "/healthcare-enquiry", testid: `${country.id}-enquiry-cta` }}
                            secondary={{ label: "Patient Support", to: "/patient-support", testid: `${country.id}-support-cta` }}
                        />
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
